import React from 'react';
import { TFunction } from 'i18next';
import moment from 'moment-timezone';
import { range } from 'lodash';

import {
	TextField,
	Stack,
	InputAdornment,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextFieldProps,
} from '@mui/material';

import {
	EMPTY_RELATIVE_BUFFER_TIME,
	RelativeBufferTime,
	getBufferTimeUnitLabel,
	BufferTimeDayTypes,
	BufferTimeDayType,
	clampBufferTimeUnitAmount,
	getBufferTimeUnitMaxAmount,
	BufferTimeDirection,
} from 'common/modules/atoms/bufferTimes';
import { DateFormatObject } from 'common/types';

interface Props {
	value?: RelativeBufferTime;
	onChange?: (value: RelativeBufferTime) => void;
	disabled?: boolean;
	direction?: BufferTimeDirection;
	dateFormat: DateFormatObject;
	t: TFunction;
}

const TIME_OPTIONS = range(24).map((i) => {
	return moment(`${i}:00`, 'HH:mm').format('HH:mm');
});

const RelativeBufferTimePicker = (props: Props) => {
	const {
		value = EMPTY_RELATIVE_BUFFER_TIME.value,
		onChange,
		disabled,
		direction,
		dateFormat,
		t,
	} = props;

	const sharedTextFieldProps: Partial<TextFieldProps> = {
		variant: 'outlined',
		margin: 'none',
		type: 'number',
		placeholder: '0',
		label: '',
	};

	const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const days = parseInt(e.target.value, 10);
		onChange?.({
			...value,
			days: clampBufferTimeUnitAmount(value.dayType, days),
		});
	};

	const handleDayTypeChange = (e: SelectChangeEvent<BufferTimeDayType>) => {
		const dayType = e.target.value as BufferTimeDayType;
		onChange?.({
			...value,
			dayType,
			days: clampBufferTimeUnitAmount(dayType, value.days),
		});
	};

	return (
		<Stack
			direction={{ xs: 'column', sm: 'row' }}
			alignItems={{ xs: 'stretch', sm: 'flex-start' }}
			spacing={2}
			sx={{ mt: 2 }}
		>
			<TextField
				{...sharedTextFieldProps}
				name="days"
				value={value.days}
				onChange={handleDaysChange}
				disabled={disabled}
				InputProps={{
					inputProps: {
						min: 0,
						max: getBufferTimeUnitMaxAmount(value.dayType),
					},
					endAdornment: (
						<InputAdornment position="end">
							<Select
								variant="standard"
								disableUnderline
								value={value.dayType}
								onChange={handleDayTypeChange}
								disabled={disabled}
								renderValue={(dayType) => {
									return `${getBufferTimeUnitLabel({
										unit: dayType,
										amount: value.days,
										t,
									})} ${
										direction === 'after'
											? t('common:times.after', 'after')
											: t('common:times.before', 'before')
									}`;
								}}
							>
								<MenuItem value={BufferTimeDayTypes.days}>
									{getBufferTimeUnitLabel({
										unit: BufferTimeDayTypes.days,
										amount: value.days,
										t,
									})}
								</MenuItem>
								<MenuItem value={BufferTimeDayTypes.weekDays}>
									{getBufferTimeUnitLabel({
										unit: BufferTimeDayTypes.weekDays,
										amount: value.days,
										t,
									})}
								</MenuItem>
								<MenuItem value={BufferTimeDayTypes.openingDays}>
									{getBufferTimeUnitLabel({
										unit: BufferTimeDayTypes.openingDays,
										amount: value.days,
										t,
									})}
								</MenuItem>
							</Select>
						</InputAdornment>
					),
				}}
			/>
			<TextField
				select
				variant="outlined"
				margin="none"
				value={value.timeOfDay}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					onChange?.({
						...value,
						timeOfDay: e.target.value,
					});
				}}
				InputLabelProps={{
					shrink: true,
					style: {
						maxWidth: 120,
					},
				}}
				SelectProps={{
					displayEmpty: false,
					native: true,
				}}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">{t('common:times.by', 'by')}</InputAdornment>
					),
				}}
				type="number"
				disabled={disabled}
			>
				{TIME_OPTIONS.map((time) => {
					return (
						<option value={time}>{moment(time, 'HH:mm').format(dateFormat.timeFormat)}</option>
					);
				})}
			</TextField>
		</Stack>
	);
};

export default RelativeBufferTimePicker;
